import { Mail, Shield, Star } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSiteAssetUrl } from "@/contexts/SiteAssetsContext";
// Local fallback for resilience
import logoFallback from "@/assets/logo.png";

const Footer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // Get logo from site assets (with local fallback)
  const logoUrl = useSiteAssetUrl('logo');
  const logo = logoUrl || logoFallback;

  const scrollToSection = (sectionId: string) => {
    const scrollToElement = () => {
      const element = document.getElementById(sectionId);
      if (element) {
        const headerOffset = 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.scrollY - headerOffset;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    };
    
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(scrollToElement, 150);
    } else {
      scrollToElement();
    }
  };

  return (
    <footer className="bg-navy text-primary-foreground py-16">
      <div className="container-custom">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center mb-4">
              <img 
                src={logo} 
                alt="MyCreditScrub Logo" 
                width={169}
                height={56}
                loading="lazy"
                decoding="async"
                style={{ aspectRatio: '169/56' }}
                className="h-12 w-auto" 
              />
            </Link>
            <p className="text-primary-foreground/70 max-w-md mb-6">
              Empowering individuals to take control of their credit with easy-to-follow DIY credit dispute tools, professional templates, and insider strategies.
            </p>
            <div className="flex items-center gap-4">
              <div className="flex -space-x-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-accent text-accent" />
                ))}
              </div>
              <span className="text-sm text-primary-foreground/70">
                Trusted by 10,000+ customers
              </span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-3 text-primary-foreground/70">
              <li>
                <button 
                  onClick={() => scrollToSection("how-it-works")} 
                  className="hover:text-primary-foreground transition-colors text-left"
                >
                  How It Works
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection("reviews")} 
                  className="hover:text-primary-foreground transition-colors text-left"
                >
                  Reviews
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection("kit-section")} 
                  className="hover:text-primary-foreground transition-colors text-left"
                >
                  The Kit
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection("faq")} 
                  className="hover:text-primary-foreground transition-colors text-left"
                >
                  FAQ
                </button>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-semibold mb-4">Legal</h4>
            <ul className="space-y-3 text-primary-foreground/70">
              <li>
                <Link to="/privacy" className="hover:text-primary-foreground transition-colors">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms" className="hover:text-primary-foreground transition-colors">
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link to="/refund" className="hover:text-primary-foreground transition-colors">
                  Refund Policy
                </Link>
              </li>
              <li>
                <Link to="/contact" className="hover:text-primary-foreground transition-colors flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Contact Us
                </Link>
              </li>
              <li>
                <Link to="/affiliate" className="hover:text-primary-foreground transition-colors">
                  Affiliate Program
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Important Disclosure */}
        <div className="pt-8 border-t border-primary-foreground/10 mb-8">
          <h4 className="font-semibold mb-4 text-sm">Important Disclosure</h4>
          <ul className="space-y-2 text-xs text-primary-foreground/60 max-w-4xl">
            <li className="flex items-start gap-2">
              <span className="text-primary-foreground/40 mt-0.5">•</span>
              <span>MyCreditScrub.com provides educational materials and template letters to help you understand and exercise your legal rights under the Fair Credit Reporting Act (FCRA).</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary-foreground/40 mt-0.5">•</span>
              <span>We are <strong className="text-primary-foreground/80">not a law firm</strong> and <strong className="text-primary-foreground/80">do not offer legal advice</strong>. For legal questions, consult a licensed attorney.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary-foreground/40 mt-0.5">•</span>
              <span>We are <strong className="text-primary-foreground/80">not a credit repair organization</strong> as defined by the Credit Repair Organizations Act (CROA). We do not perform credit repair services on your behalf.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary-foreground/40 mt-0.5">•</span>
              <span>Our templates are for personal use only; resale, redistribution, or sharing is prohibited.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary-foreground/40 mt-0.5">•</span>
              <span>We do <strong className="text-primary-foreground/80">not guarantee score increases, deletions, or specific outcomes</strong>. Results depend on your individual circumstances and the accuracy of your information.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary-foreground/40 mt-0.5">•</span>
              <span>Use the free government site <a href="https://www.annualcreditreport.com/" target="_blank" rel="noopener noreferrer" className="text-primary-foreground/80 underline hover:text-primary-foreground">AnnualCreditReport.com</a> to obtain your official credit reports.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary-foreground/40 mt-0.5">•</span>
              <span>For personalized financial or legal advice, consult a qualified professional.</span>
            </li>
          </ul>
        </div>

        {/* Bottom bar */}
        <div className="pt-8 border-t border-primary-foreground/10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-primary-foreground/60 text-center md:text-left">
              © {new Date().getFullYear()} MyCreditScrub.com. All rights reserved.
            </p>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-sm text-primary-foreground/60">
                <Shield className="w-4 h-4" />
                <span>Secure Payments</span>
              </div>
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-3 h-3 fill-accent text-accent" />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
